/* eslint no-underscore-dangle: ["error", { "allow": ["__CANCEL__"] }] */
import axios, { AxiosInstance } from 'axios';
import environment from '@shared/environments/environment';
import {
  TOKEN_NAME,
  RETURN_URI,
  NO_DEFAULT_ERRORS_HEADER,
  REDIRECT_URL
} from '@shared/models/constants';
import { formQueryString, parseErrors } from '@shared/helpers/common.helper';
import { Observer, SERVER_ERROR } from '@shared/helpers/observer';
import { ServerErrorsModel } from '@shared/models/Common/ServerErrorsModel';

export class ApiService {
  instance: AxiosInstance;

  controllerPath: string;

  private sourcesList: { [key: string]: any } = {};

  constructor(controllerPath: string) {
    this.controllerPath = controllerPath;
    this.instance = axios.create({
      baseURL: environment.baseUri,
      withCredentials: false,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    this.instance.interceptors.request.use((config) => {
      let { url, params } = config;
      if (config.method === 'get' && params) {
        url += `?${formQueryString(params)}`;
        params = undefined;
      }

      return {
        ...config,
        url,
        params,
        headers: {
          ...config.headers,
          Authorization: localStorage.getItem(TOKEN_NAME)
        }
      };
    });
    this.instance.interceptors.response.use(
      (response) => {
        const parsedResponse = JSON.parse(JSON.stringify(response.data),
          (key: string, value: any) => {
            switch (key.toLocaleLowerCase()) {
              case 'studysubjects':
              case 'languages':
              case 'agegroups':
                return JSON.parse(value);
              default:
                return value;
            }
          });
        return parsedResponse;
      },
      (error) => {
        if (error.__CANCEL__) return null;

        const noErrors = error.request.requestHeaders?.[NO_DEFAULT_ERRORS_HEADER];

        if (error.response.status === 401) {
          localStorage.setItem(TOKEN_NAME, '');
          const returnURI = localStorage.getItem(RETURN_URI)
            ? localStorage.getItem(RETURN_URI)
            : REDIRECT_URL;
          if (returnURI) {
            window.location.href =
              returnURI?.length && returnURI !== 'undefined' ? returnURI : REDIRECT_URL;
          } else {
            window.location.reload();
          }
        } else if (error.response.status === 403) {
          Observer.emit(SERVER_ERROR, ['This action is forbidden for current user']);
        } else if (!noErrors && error.response.data) {
          const errorsList = parseErrors(error.response.data.errors as ServerErrorsModel);
          Observer.emit(SERVER_ERROR, errorsList);
        } else if (!error.response.data) {
          Observer.emit(SERVER_ERROR, ['Server error occurred']);
        }

        return Promise.reject(error);
      }
    );
  }

  deleteById(id: number, url?: string) {
    const query = `${this.controllerPath}${url || ''}/delete?id=${id}`;
    return this.instance.delete(query);
  }

  deleteMany(idList: number[]) {
    const query = `${this.controllerPath}/deleteMany`;
    return this.instance.post(
      query,
      idList.map((x) => ({ id: x }))
    );
  }

  get(url: string, ...opts: any[]): Promise<any> {
    return this.instance.get(`${this.controllerPath}${url}`, ...opts);
  }

  post(url: string, ...opts: any[]): Promise<any> {
    return this.instance.post(`${this.controllerPath}${url}`, ...opts);
  }

  put(url: string, ...opts: any[]): Promise<any> {
    return this.instance.put(`${this.controllerPath}${url}`, ...opts);
  }

  delete(url: string, ...opts: any): Promise<void> {
    return this.instance.delete(`${this.controllerPath}${url}`, ...opts);
  }

  getCancelToken(key: string) {
    const source = axios.CancelToken.source();

    if (this.sourcesList[key]) {
      this.sourcesList[key].cancel();
    }
    this.sourcesList[key] = source;
    return source.token;
  }
}
